import { useCallback, useMemo, useState } from 'react';

import { SelectableItem, SelectableItemsGroup, SelectedKeyValues } from './types';

const getSelectedIds = <T = string>(selectedKeyValues: SelectedKeyValues<T>): T[] =>
  Object.values(selectedKeyValues).reduce((acc, idsArray) => [...acc, ...idsArray], []);

export const useSelectableAccordion = <T = string>(
  items: SelectableItemsGroup<T>[],
  onSelectionChange?: (selectedIds: T[]) => void,
) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [selectedKeyValues, setSelectedKeyValues] = useState<SelectedKeyValues<T>>({});

  const handleGroupClick = useCallback(
    groupId => {
      const isExpanded = expandedGroups.includes(groupId);

      if (isExpanded) {
        setExpandedGroups(prev => prev.filter(prevGroup => prevGroup !== groupId));
        return;
      }

      setExpandedGroups(prev => [...prev, groupId]);
    },
    [expandedGroups],
  );

  const handleDeselectItem = useCallback(
    ({ groupId, optionId }) => {
      const updatedState = {
        ...selectedKeyValues,
        [groupId]: selectedKeyValues[groupId].filter(prevOpt => prevOpt !== optionId),
      };
      setSelectedKeyValues(updatedState);
      const selectedIds = getSelectedIds<T>(updatedState);
      onSelectionChange?.(selectedIds);
    },
    [onSelectionChange, selectedKeyValues],
  );

  const handleItemClick = useCallback(
    ({
      groupId,
      type,
      option,
    }: {
      groupId: string;
      type: SelectableItemsGroup<T>['type'];
      option: SelectableItem<T>;
    }) => {
      const isChecked = selectedKeyValues[groupId]?.includes(option.id);

      if (isChecked && type === 'checkbox') {
        handleDeselectItem({ groupId, optionId: option.id });
        return;
      }

      const updatedState =
        type === 'radio'
          ? { ...selectedKeyValues, [groupId]: [option.id] }
          : { ...selectedKeyValues, [groupId]: [...(selectedKeyValues[groupId] ?? []), option.id] };
      setSelectedKeyValues(updatedState);
      const selectedIds = getSelectedIds<T>(updatedState);
      onSelectionChange?.(selectedIds);
    },
    [handleDeselectItem, onSelectionChange, selectedKeyValues],
  );

  const expandAllGroups = useCallback(() => {
    setExpandedGroups(items.map(({ groupId }) => groupId));
  }, [items]);

  const handleClearAll = useCallback(() => {
    setSelectedKeyValues({});
    expandAllGroups();
    onSelectionChange?.([]);
  }, [expandAllGroups, onSelectionChange]);

  const selectedIds = useMemo(() => getSelectedIds(selectedKeyValues), [selectedKeyValues]);

  return {
    handleClearAll,
    handleGroupClick,
    handleItemClick,
    expandedGroups,
    expandAllGroups,
    selectedIds,
    selectedKeyValues,
    handleDeselectItem,
  };
};
